// src/utils/contractHistoryStore.js
// Firestore reads and writes for Contract Fell Through and Restore This Contract.
//
// Saved contracts live at deals/{dealId}/contractHistory/{historyId}, in their own
// documents. That keeps the app-launch download of the deals collection small, and
// a whole-deal merge write can never overwrite them. The recursive
// `match /deals/{doc=**}` rule in firestore.rules already covers this path, so no
// rules deploy is needed. Do not rename the collection to "timeline": that name
// has its own collection-group rule.
//
// Every decision (what is saved, cleared and restored) lives in the pure planner,
// src/utils/contractFellThrough.js. This file only reads fresh data, turns a plan
// into one all-or-nothing batch, and reports back.
// Never import ./firebasefunctions here: firebasefunctions.js imports this file.
//
// Every write to a deal document retires the sign-in copy of every deal first
// (src/utils/launchData.js), so a screen opened after the save never shows the
// contract that was just cleared or restored. Pinned by
// src/utils/__tests__/dealWritesRetireLaunchCopy.test.js, which is why each deal
// write below targets a variable made with doc(firestored, "deals", ...).

import { collection, deleteField, doc, getDoc, getDocs, onSnapshot, orderBy, query } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { DELETE_FIELD, planFellThrough, planRestore } from "./contractFellThrough";
import { isBuyerSideRole } from "./contractRoles";
import { retireLaunchRead } from "./launchData";
import { writeBatch } from "./lookOnlyFirestore";
import { assertCanWrite } from "./lookOnly";

export const CONTRACT_HISTORY = "contractHistory";

// Firestore allows 500 writes per batch; stay well under it.
const DELETE_BATCH_SIZE = 400;

const hasId = (value) => value !== undefined && value !== null && String(value) !== "";
const dealRef = (dealId) => doc(firestored, "deals", String(dealId));
const historyCollection = (dealId) =>
  collection(firestored, "deals", String(dealId), CONTRACT_HISTORY);
const historyRef = (dealId, historyId) =>
  doc(firestored, "deals", String(dealId), CONTRACT_HISTORY, String(historyId));

// The planner marks removals with DELETE_FIELD; Firestore needs deleteField().
export function toFirestorePatch(patch) {
  const out = {};
  Object.keys(patch || {}).forEach((key) => {
    out[key] = patch[key] === DELETE_FIELD ? deleteField() : patch[key];
  });
  return out;
}

// The deal as it looks after the write, for the global deals store and the page.
const applyPatchLocally = (deal, patch) => {
  const next = { ...deal };
  Object.keys(patch).forEach((key) => {
    if (patch[key] === DELETE_FIELD) delete next[key];
    else next[key] = patch[key];
  });
  return next;
};

// Always the database copy, never the page's in-memory copy. A listing that no
// longer exists is refused: writing to it would create an empty husk deal.
const readDeal = async (dealId) => {
  if (!hasId(dealId)) throw new Error("deal-missing");
  const snap = await getDoc(dealRef(dealId));
  if (!snap.exists()) throw new Error("deal-missing");
  return { ...snap.data() };
};

export async function markContractFellThrough({ dealId, stage, reasons = [], note = "", actor } = {}) {
  assertCanWrite();
  const dealBefore = await readDeal(dealId);
  const { historyId, historyDoc, dealPatch } = planFellThrough(dealBefore, {
    stage,
    reasons,
    note,
    actor,
    now: Date.now(),
    isBuyerSideRole,
  });

  const dealToClear = doc(firestored, "deals", String(dealId));
  const batch = writeBatch(firestored);
  batch.set(historyRef(dealId, historyId), historyDoc);
  // update, never set-merge: map fields like taskStatusTracker must be REPLACED so
  // the removed Under Contract / Pending / Closed keys are really gone.
  retireLaunchRead("deals");
  batch.update(dealToClear, toFirestorePatch(dealPatch));
  await batch.commit();

  return { dealBefore, historyDoc, dealAfter: applyPatchLocally(dealBefore, dealPatch) };
}

export async function restoreContract({ dealId, historyId, actor } = {}) {
  assertCanWrite();
  const deal = await readDeal(dealId);
  if (!hasId(historyId)) throw new Error("history-missing");
  const historySnap = await getDoc(historyRef(dealId, historyId));
  if (!historySnap.exists()) throw new Error("history-missing");
  const historyDoc = { ...historySnap.data(), id: historySnap.id };

  const plan = planRestore(deal, historyDoc, { actor, now: Date.now() });
  if (!plan.ok) return { ok: false, reason: plan.reason, dealAfter: deal };

  const dealToRestore = doc(firestored, "deals", String(dealId));
  const batch = writeBatch(firestored);
  retireLaunchRead("deals");
  batch.update(dealToRestore, toFirestorePatch(plan.dealPatch));
  batch.update(historyRef(dealId, historyId), plan.historyPatch);
  await batch.commit();

  await unarchivePeoplePutBack(historyDoc);
  return { ok: true, reason: "", dealAfter: applyPatchLocally(deal, plan.dealPatch) };
}

// The buyer's-side people a restore puts back on the deal. Any of them the Follow
// Up Boss Leftovers card archived while the contract sat saved comes back to the
// contact lists. Best-effort: the restore has already landed. Loaded on demand,
// because fubLeftoversStore.js imports ./firebasefunctions (see the top of this file).
async function unarchivePeoplePutBack(historyDoc) {
  try {
    const removed = historyDoc.contactsRemoved || {};
    const fromTypes = (Array.isArray(removed.contactType) ? removed.contactType : []).map(
      (entry) => (entry && entry.contactId ? String(entry.contactId) : ""),
    );
    const fromSyncIds = (Array.isArray(removed.contactSyncId) ? removed.contactSyncId : []).map((id) =>
      hasId(id) ? String(id) : "",
    );
    const ids = [...new Set([...fromTypes, ...fromSyncIds].filter(Boolean))];
    if (!ids.length) return;
    const { unarchiveFubLeftoverContacts } = await import("./fubLeftoversStore");
    await unarchiveFubLeftoverContacts(ids);
  } catch (err) {
    console.error("Bringing back a restored contract's archived contacts failed:", err);
  }
}

// Live list of a deal's saved contracts, newest first. onError is optional; a
// failed listen is never reported to onChange as an empty list.
export function subscribeContractHistory(dealId, onChange, onError) {
  if (!hasId(dealId)) return () => {};
  const newestFirst = query(historyCollection(dealId), orderBy("createdAt", "desc"));
  return onSnapshot(
    newestFirst,
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }))),
    (err) => {
      console.error("subscribeContractHistory failed:", err);
      if (typeof onError === "function") onError(err);
    },
  );
}

// Firestore never deletes a subcollection along with its parent document, so the
// permanent delete (firebasefunctions.js permanentlyDeleteDeal) calls this first.
export async function deleteContractHistory(dealId) {
  assertCanWrite();
  if (!hasId(dealId)) return 0;
  const snap = await getDocs(historyCollection(dealId));
  const refs = snap.docs.map((d) => d.ref);
  for (let i = 0; i < refs.length; i += DELETE_BATCH_SIZE) {
    const batch = writeBatch(firestored);
    // launch-copy-safe: these are contractHistory documents, not deal documents,
    // and the sign-in copy holds only the deals collection itself.
    refs.slice(i, i + DELETE_BATCH_SIZE).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  return refs.length;
}
